import { useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { Pause, Play, Plus, Trash2 } from 'lucide-react';
import { Badge, Button, EmptyState, Field, Input, Section, Select, Spinner, type Tone } from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import { ContentReviewPanel, reviewTone } from '../components/ContentReview';
import { ProductMultiPicker } from '../components/ProductMultiPicker';
import { CompanyMultiPicker, type PickedCompany } from '../components/CompanyMultiPicker';
import { api, ApiError } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { bpsLabel, minorToInput, toMinor, useMoney } from '../lib/money';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type { CollectionRow, Promotion, PromotionDetail, SegmentRow } from '../lib/types';
import { StorePicker, useStoreChoice } from './payments';
import { percentToBps, useGroups } from './pricing';
import { useCatalogRefs } from './products';

/**
 * Fiyatlar → Otomatik indirimler (F6-11, ADR-092): kupon gerektirmeyen kampanyalar. Taslak → yayına al (iki adımlı
 * doğrulama) → duraklat. Yayındaki kampanya değiştirilmez. Fiyatı kampanyadan önceki 10 günde artırılan ürünler
 * indirim almaz ve listelenir (dürüst indirim). Kampanya adı müşteriye görünür; sağlık beyanı uzman onayı ister.
 */

type Kind = Promotion['kind'];
type Phase = Promotion['phase'];
const KINDS: Kind[] = ['item_discount', 'cart_tiers', 'buy_x_pay_y', 'free_shipping'];
const CURRENCIES = ['TRY', 'EUR', 'USD', 'GBP', 'AED', 'SAR', 'AZN'];
const ROUTE = '/otomatik-indirimler';
const phaseTone: Record<Phase, Tone> = {
  draft: 'neutral',
  scheduled: 'slate',
  running: 'moss',
  paused: 'amber',
  ended: 'slate',
};

function toLocalInput(v: string | null): string {
  if (!v) return '';
  const d = new Date(v);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}
const bpsInput = (bps: number) => bpsLabel(bps).replace('%', '');

interface Draft {
  name: string;
  label: string;
  kind: Kind;
  percent: string;
  buy: string;
  pay: string;
  minSubtotal: string;
  basis: 'subtotal' | 'quantity';
  tierType: 'percentage' | 'fixed_amount';
  tiers: Array<{ min: string; value: string }>;
  currency: string;
  productIds: string[];
  categoryIds: string[];
  brandIds: string[];
  collectionIds: string[];
  audience: Promotion['audience'];
  customerGroupIds: string[];
  segmentIds: string[];
  /** Bayiye özel kampanya (F7-10): seçilen bayi firmaları. */
  companies: PickedCompany[];
  priority: string;
  stacking: Promotion['stacking'];
  allowCoupon: boolean;
  startsAt: string;
  endsAt: string;
}

const emptyDraft = (): Draft => ({
  name: '',
  label: '',
  kind: 'item_discount',
  percent: '',
  buy: '3',
  pay: '2',
  minSubtotal: '',
  basis: 'subtotal',
  tierType: 'percentage',
  tiers: [{ min: '', value: '' }],
  currency: 'TRY',
  productIds: [],
  categoryIds: [],
  brandIds: [],
  collectionIds: [],
  audience: 'all',
  customerGroupIds: [],
  segmentIds: [],
  companies: [],
  priority: '0',
  stacking: 'combinable',
  allowCoupon: true,
  startsAt: '',
  endsAt: '',
});

function draftOf(p: Promotion & { companies?: PickedCompany[] }): Draft {
  const d = emptyDraft();
  const r = p.rules;
  const base: Draft = {
    ...d,
    name: p.name,
    label: p.label,
    kind: p.kind,
    currency: p.currency ?? 'TRY',
    productIds: p.targets.productIds,
    categoryIds: p.targets.categoryIds,
    brandIds: p.targets.brandIds,
    collectionIds: p.targets.collectionIds,
    audience: p.audience,
    customerGroupIds: p.customerGroupIds,
    segmentIds: p.segmentIds,
    companies: p.companies ?? [],
    priority: String(p.priority),
    stacking: p.stacking,
    allowCoupon: p.allowCoupon,
    startsAt: toLocalInput(p.startsAt),
    endsAt: toLocalInput(p.endsAt),
  };
  if (r.kind === 'item_discount') return { ...base, percent: bpsInput(r.valueBps) };
  if (r.kind === 'buy_x_pay_y') return { ...base, buy: String(r.buy), pay: String(r.pay) };
  if (r.kind === 'free_shipping') return { ...base, minSubtotal: minorToInput(r.minSubtotalMinor) };
  return {
    ...base,
    basis: r.basis,
    tierType: r.type,
    tiers: r.tiers.map((x) => ({
      min: r.basis === 'subtotal' ? minorToInput(x.min) : String(x.min),
      value: r.type === 'percentage' ? bpsInput(x.value) : minorToInput(x.value),
    })),
  };
}

const needsCurrency = (d: Draft) =>
  (d.kind === 'cart_tiers' && (d.basis === 'subtotal' || d.tierType === 'fixed_amount')) ||
  (d.kind === 'free_shipping' && !!d.minSubtotal.trim());

function rulesOf(d: Draft) {
  const int = (s: string) => (/^\d+$/.test(s.trim()) ? Number(s.trim()) : -1);
  switch (d.kind) {
    case 'item_discount':
      return { kind: d.kind, valueBps: percentToBps(d.percent) ?? 0 };
    case 'buy_x_pay_y':
      return { kind: d.kind, buy: int(d.buy), pay: int(d.pay) };
    case 'free_shipping':
      return {
        kind: d.kind,
        minSubtotalMinor: d.minSubtotal.trim() ? (toMinor(d.minSubtotal, d.currency) ?? -1) : null,
      };
    case 'cart_tiers':
      return {
        kind: d.kind,
        basis: d.basis,
        type: d.tierType,
        tiers: d.tiers.map((x) => ({
          min: d.basis === 'subtotal' ? (toMinor(x.min, d.currency) ?? -1) : int(x.min),
          value:
            d.tierType === 'percentage' ? (percentToBps(x.value) ?? 0) : (toMinor(x.value, d.currency) ?? 0),
        })),
      };
  }
}

function bodyOf(d: Draft) {
  return {
    name: d.name,
    label: d.label,
    rules: rulesOf(d),
    targets: {
      productIds: d.productIds,
      categoryIds: d.categoryIds,
      brandIds: d.brandIds,
      collectionIds: d.collectionIds,
    },
    currency: needsCurrency(d) ? d.currency : null,
    audience: d.companies.length ? 'dealers' : d.audience,
    customerGroupIds: d.customerGroupIds,
    segmentIds: d.segmentIds,
    companyIds: d.companies.map((c) => c.id),
    priority: Number(d.priority) || 0,
    stacking: d.stacking,
    allowCoupon: d.allowCoupon,
    startsAt: d.startsAt ? new Date(d.startsAt).toISOString() : null,
    endsAt: d.endsAt ? new Date(d.endsAt).toISOString() : null,
  };
}

/** Müşterinin göreceği koşul metni (vitrindeki rozetle aynı çeviri anahtarları). */
function useRuleLines() {
  const t = useT();
  const money = useMoney();
  const pct = (bps: number) => bpsInput(bps);
  return (p: Pick<Promotion, 'rules' | 'currency'>): string[] => {
    const r = p.rules;
    const cur = p.currency ?? 'TRY';
    if (r.kind === 'item_discount') return [t('storefront.promo.itemDiscount', { percent: pct(r.valueBps) })];
    if (r.kind === 'buy_x_pay_y')
      return [t('storefront.promo.buyXPayY', { buy: r.buy, pay: r.pay, free: r.buy - r.pay })];
    if (r.kind === 'free_shipping')
      return [
        r.minSubtotalMinor
          ? t('storefront.promo.freeShippingOver', { min: money(r.minSubtotalMinor, cur) })
          : t('storefront.promo.freeShipping'),
      ];
    return [...r.tiers]
      .sort((a, b) => a.min - b.min)
      .map((x) => {
        const params = {
          min: r.basis === 'subtotal' ? money(x.min, cur) : String(x.min),
          percent: pct(x.value),
          value: money(x.value, cur),
        };
        if (r.basis === 'subtotal')
          return r.type === 'percentage'
            ? t('storefront.promo.tierSubtotalPercent', params)
            : t('storefront.promo.tierSubtotalFixed', params);
        return r.type === 'percentage'
          ? t('storefront.promo.tierQuantityPercent', params)
          : t('storefront.promo.tierQuantityFixed', params);
      });
  };
}

// ---------------------------------------------------------------------------- Liste

export function PromotionsPage() {
  const t = useT();
  const money = useMoney();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const navigate = useNavigate();
  const { stores, loading, storeId, setStoreId } = useStoreChoice();
  const list = useQuery({
    queryKey: ['promotions'],
    queryFn: () => api.get<Promotion[]>('/v1/admin/promotions'),
  });
  if (loading) return <Spinner label={t('common.loading')} />;
  const rows = (list.data ?? []).filter((p) => p.storeId === storeId);
  return (
    <PageFrame
      helpKey="page.promotions"
      actions={
        can('pricing.price.write') &&
        storeId && (
          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              onClick={() => void navigate({ to: `${ROUTE}/$id`, params: { id: 'yeni' } })}
            >
              <Plus className="h-4 w-4" aria-hidden />
              {t('admin.promotions.create')}
            </Button>
            <HelpTip k="promotions.create" />
          </div>
        )
      }
    >
      {!stores.length ? (
        <EmptyState title={t('admin.payments.noStore')} description={t('admin.payments.noStoreLead')} />
      ) : (
        <div className="space-y-6">
          <p className="max-w-[70ch] text-sm text-ink-2">{t('admin.promotions.lead')}</p>
          <StorePicker stores={stores} storeId={storeId} onChange={setStoreId} />
          {!list.data ? (
            <Spinner label={t('common.loading')} />
          ) : !rows.length ? (
            <EmptyState
              title={t('admin.promotions.emptyTitle')}
              description={t('admin.promotions.emptyLead')}
            />
          ) : (
            <div className="overflow-x-auto rounded-md border border-line">
              <table className="w-full text-sm" aria-label={t('admin.promotions.listLabel')}>
                <thead className="bg-surface-2 text-ink-2">
                  <tr>
                    <th className="px-3 py-2 text-start font-medium">{t('admin.promotions.colName')}</th>
                    <th className="px-3 py-2 text-start font-medium">{t('admin.promotions.colKind')}</th>
                    <th className="px-3 py-2 text-start font-medium">{t('admin.promotions.colStatus')}</th>
                    <th className="px-3 py-2 text-end font-medium">{t('admin.promotions.colUsage')}</th>
                    <th className="px-3 py-2 text-end font-medium">{t('admin.promotions.colDates')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((p) => (
                    <tr key={p.id} className="border-t border-line align-top">
                      <td className="px-3 py-2">
                        <Link
                          to={`${ROUTE}/$id`}
                          params={{ id: p.id }}
                          className="font-medium text-pine hover:underline"
                        >
                          {p.name}
                        </Link>
                        <p className="text-ink-3">{p.label}</p>
                        {p.companyIds.length > 0 && (
                          <Badge tone="pine" className="mt-1">
                            {t('admin.dealerCampaigns.forCompanies', { count: p.companyIds.length })}
                          </Badge>
                        )}
                      </td>
                      <td className="px-3 py-2">{t(`admin.promotions.kinds.${p.kind}`)}</td>
                      <td className="px-3 py-2">
                        <Badge tone={phaseTone[p.phase]}>{t(`admin.promotions.phases.${p.phase}`)}</Badge>
                      </td>
                      <td className="px-3 py-2 text-end tabular-nums">
                        {p.usage.length
                          ? p.usage.map((u) => (
                              <span key={u.currency} className="block">
                                {t('admin.promotions.usageShort', {
                                  orders: u.orders,
                                  discount: money(u.discountMinor, u.currency),
                                })}
                              </span>
                            ))
                          : '—'}
                      </td>
                      <td className="px-3 py-2 text-end text-ink-2">
                        {p.startsAt || p.endsAt
                          ? `${p.startsAt ? formatDate(p.startsAt) : '…'} – ${p.endsAt ? formatDate(p.endsAt) : '…'}`
                          : t('admin.promotions.noDates')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </PageFrame>
  );
}

// ---------------------------------------------------------------------------- Düzenleyici

export function PromotionEditorPage() {
  const { id } = useParams({ from: '/app/otomatik-indirimler/$id' });
  const isNew = id === 'yeni';
  const t = useT();
  const q = useQuery({
    queryKey: ['promotion', id],
    queryFn: () => api.get<PromotionDetail>(`/v1/admin/promotions/${id}`),
    enabled: !isNew,
  });
  if (!isNew && !q.data) return <Spinner label={t('common.loading')} />;
  return <PromotionEditor key={q.data?.id ?? 'yeni'} promotion={q.data ?? null} />;
}

export function CheckList({
  legend,
  help,
  items,
  value,
  onChange,
  empty,
}: {
  legend: string;
  help: ReactNode;
  items: Array<{ id: string; name: string }>;
  value: string[];
  onChange: (v: string[]) => void;
  empty?: string;
}) {
  return (
    <fieldset className="space-y-1.5">
      <legend className="mb-1 flex items-center gap-1 text-sm font-medium">
        {legend}
        {help}
      </legend>
      {!items.length && empty && <p className="text-sm text-ink-3">{empty}</p>}
      <div className="max-h-48 space-y-1 overflow-y-auto">
        {items.map((i) => (
          <label key={i.id} className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={value.includes(i.id)}
              onChange={(e) =>
                onChange(e.target.checked ? [...value, i.id] : value.filter((x) => x !== i.id))
              }
            />
            {i.name}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function PromotionEditor({ promotion }: { promotion: PromotionDetail | null }) {
  const t = useT();
  const money = useMoney();
  const { formatDate } = useI18n();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canWrite = can('pricing.price.write');
  const { stores, storeId: chosenStore, setStoreId } = useStoreChoice();
  const storeId = promotion?.storeId ?? chosenStore;
  const editable = canWrite && (!promotion || promotion.status !== 'active');
  const { brands, categories } = useCatalogRefs();
  const groups = useGroups();
  const collections = useQuery({
    queryKey: ['collections'],
    queryFn: () => api.get<CollectionRow[]>('/v1/admin/collections'),
  });
  const segments = useQuery({
    queryKey: ['segments'],
    queryFn: () => api.get<SegmentRow[]>('/v1/admin/segments'),
    enabled: can('marketing.audience.read'),
  });
  const ruleLines = useRuleLines();
  const [draft, setDraft] = useState<Draft>(() => (promotion ? draftOf(promotion) : emptyDraft()));
  useEffect(() => {
    if (promotion) setDraft(draftOf(promotion));
  }, [promotion]);
  const set = (p: Partial<Draft>) => setDraft((d) => ({ ...d, ...p }));
  const dirty = !promotion || JSON.stringify(draft) !== JSON.stringify(draftOf(promotion));
  const complete = draft.name.trim().length >= 2 && draft.label.trim().length >= 2;
  const invalidate = [['promotions'], ...(promotion ? [['promotion', promotion.id]] : [])];

  const save = useApiMutation(
    () =>
      promotion
        ? api.patch<Promotion>(`/v1/admin/promotions/${promotion.id}`, {
            ...bodyOf(draft),
            version: promotion.version,
          })
        : api.post<Promotion>('/v1/admin/promotions', { ...bodyOf(draft), storeId }),
    {
      invalidate,
      success: t('admin.promotions.saved'),
      onSuccess: (r) => {
        if (!promotion) void navigate({ to: `${ROUTE}/$id`, params: { id: r.id } });
      },
    },
  );
  const activate = useApiMutation(
    () =>
      api.post<Promotion>(`/v1/admin/promotions/${promotion!.id}/activate`, { version: promotion!.version }),
    { invalidate, success: t('admin.promotions.activated') },
  );
  const pause = useApiMutation(
    () => api.post<Promotion>(`/v1/admin/promotions/${promotion!.id}/pause`, { version: promotion!.version }),
    { invalidate, success: t('admin.promotions.paused') },
  );
  const review = useApiMutation(
    (v: { decision: 'approved' | 'rejected'; note: string }) =>
      api.post<Promotion>(`/v1/admin/promotions/${promotion!.id}/review`, {
        decision: v.decision,
        note: v.note || null,
        contentHash: promotion!.review.contentHash,
        version: promotion!.version,
      }),
    { invalidate, success: t('admin.pages.reviewedToast') },
  );
  const remove = useApiMutation(() => api.del(`/v1/admin/promotions/${promotion!.id}`), {
    invalidate: [['promotions']],
    success: t('admin.promotions.deleted'),
    onSuccess: () => void navigate({ to: ROUTE }),
  });
  const fe = (f: string) => fieldError(save.error, f);
  const actionError =
    [activate.error, pause.error].find((e) => e instanceof ApiError && !e.errors.length) ?? null;
  const targetable = draft.kind !== 'free_shipping';
  const preview = { rules: rulesOf(draft), currency: needsCurrency(draft) ? draft.currency : null } as Pick<
    Promotion,
    'rules' | 'currency'
  >;

  return (
    <PageFrame
      helpKey="page.promotion_editor"
      title={promotion ? promotion.name : t('admin.promotions.create')}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          {editable && (
            <>
              <Button
                variant={promotion ? 'secondary' : 'primary'}
                onClick={() => save.mutate(undefined)}
                disabled={!dirty || !complete || !storeId || save.isPending}
              >
                {promotion ? t('admin.promotions.save') : t('admin.promotions.createConfirm')}
              </Button>
              <HelpTip k="promotions.save" />
            </>
          )}
          {promotion && canWrite && promotion.status !== 'active' && (
            <>
              <Button
                variant="primary"
                onClick={() => activate.mutate(undefined)}
                disabled={dirty || !promotion.usable || activate.isPending}
              >
                <Play className="h-4 w-4" aria-hidden />
                {t('admin.promotions.activate')}
              </Button>
              <HelpTip k="promotions.activate" />
            </>
          )}
          {promotion && canWrite && promotion.status === 'active' && (
            <>
              <Button onClick={() => pause.mutate(undefined)} disabled={pause.isPending}>
                <Pause className="h-4 w-4" aria-hidden />
                {t('admin.promotions.pause')}
              </Button>
              <HelpTip k="promotions.pause" />
            </>
          )}
        </div>
      }
    >
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Link to={ROUTE} className="text-pine hover:underline">
          {t('admin.promotions.back')}
        </Link>
        {promotion && (
          <Badge tone={phaseTone[promotion.phase]}>{t(`admin.promotions.phases.${promotion.phase}`)}</Badge>
        )}
        {promotion && (promotion.review.required || promotion.review.status !== 'not_required') && (
          <Badge tone={reviewTone[promotion.review.status]}>
            {t(`admin.pages.reviews.${promotion.review.status}`)}
          </Badge>
        )}
        {promotion && editable && dirty && <Badge tone="amber">{t('admin.promotions.unsaved')}</Badge>}
      </div>
      {promotion?.status === 'active' && (
        <p className="rounded-md border border-line bg-surface-2 px-3 py-2 text-sm">
          {t('admin.promotions.liveLocked')}
        </p>
      )}
      {promotion && !promotion.usable && (
        <p className="rounded-md border border-amber/30 bg-amber-tint px-3 py-2 text-sm">
          {t('admin.promotions.reviewRequired')}
        </p>
      )}
      {actionError && (
        <p role="alert" className="text-sm font-medium text-rose">
          {actionError.title}
        </p>
      )}
      {!promotion && <StorePicker stores={stores} storeId={storeId} onChange={setStoreId} />}

      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <fieldset disabled={!editable} className="grid content-start gap-4">
          <legend className="sr-only">{t('admin.promotions.editor')}</legend>
          <Field
            label={t('admin.promotions.name')}
            description={t('admin.promotions.nameLead')}
            help={<HelpTip k="promotions.name" />}
            error={fe('name')}
            required
          >
            {(p) => (
              <Input
                {...p}
                value={draft.name}
                maxLength={120}
                onChange={(e) => set({ name: e.target.value })}
              />
            )}
          </Field>
          <Field
            label={t('admin.promotions.label')}
            description={t('admin.promotions.labelLead', { n: draft.label.length, max: 80 })}
            help={<HelpTip k="promotions.label" />}
            error={fe('label')}
            required
          >
            {(p) => (
              <Input
                {...p}
                value={draft.label}
                maxLength={80}
                onChange={(e) => set({ label: e.target.value })}
              />
            )}
          </Field>
          <Field label={t('admin.promotions.kind')} help={<HelpTip k="promotions.kind" />}>
            {(p) => (
              <Select {...p} value={draft.kind} onChange={(e) => set({ kind: e.target.value as Kind })}>
                {KINDS.map((k) => (
                  <option key={k} value={k}>
                    {t(`admin.promotions.kinds.${k}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <p className="text-sm text-ink-3">{t(`admin.promotions.kindLeads.${draft.kind}`)}</p>

          {draft.kind === 'item_discount' && (
            <Field
              label={t('admin.promotions.percent')}
              help={<HelpTip k="promotions.percent" />}
              error={fe('rules.valueBps')}
              required
            >
              {(p) => (
                <Input
                  {...p}
                  inputMode="decimal"
                  className="max-w-[10rem]"
                  value={draft.percent}
                  onChange={(e) => set({ percent: e.target.value })}
                />
              )}
            </Field>
          )}
          {draft.kind === 'buy_x_pay_y' && (
            <div className="grid gap-3 sm:grid-cols-2">
              <Field
                label={t('admin.promotions.buy')}
                help={<HelpTip k="promotions.buy" />}
                error={fe('rules.buy')}
              >
                {(p) => (
                  <Input
                    {...p}
                    inputMode="numeric"
                    value={draft.buy}
                    onChange={(e) => set({ buy: e.target.value })}
                  />
                )}
              </Field>
              <Field
                label={t('admin.promotions.pay')}
                help={<HelpTip k="promotions.pay" />}
                error={fe('rules.pay')}
              >
                {(p) => (
                  <Input
                    {...p}
                    inputMode="numeric"
                    value={draft.pay}
                    onChange={(e) => set({ pay: e.target.value })}
                  />
                )}
              </Field>
            </div>
          )}
          {draft.kind === 'free_shipping' && (
            <Field
              label={t('admin.promotions.minSubtotal')}
              description={t('admin.promotions.minSubtotalLead')}
              optionalLabel={t('common.optional')}
              help={<HelpTip k="promotions.min_subtotal" />}
              error={fe('rules.minSubtotalMinor')}
            >
              {(p) => (
                <Input
                  {...p}
                  inputMode="decimal"
                  className="max-w-[12rem]"
                  value={draft.minSubtotal}
                  onChange={(e) => set({ minSubtotal: e.target.value })}
                />
              )}
            </Field>
          )}
          {draft.kind === 'cart_tiers' && (
            <div className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label={t('admin.promotions.basis')} help={<HelpTip k="promotions.basis" />}>
                  {(p) => (
                    <Select
                      {...p}
                      value={draft.basis}
                      onChange={(e) => set({ basis: e.target.value as Draft['basis'] })}
                    >
                      <option value="subtotal">{t('admin.promotions.bases.subtotal')}</option>
                      <option value="quantity">{t('admin.promotions.bases.quantity')}</option>
                    </Select>
                  )}
                </Field>
                <Field label={t('admin.promotions.tierType')} help={<HelpTip k="promotions.tier_type" />}>
                  {(p) => (
                    <Select
                      {...p}
                      value={draft.tierType}
                      onChange={(e) => set({ tierType: e.target.value as Draft['tierType'] })}
                    >
                      <option value="percentage">{t('admin.discounts.types.percentage')}</option>
                      <option value="fixed_amount">{t('admin.discounts.types.fixed_amount')}</option>
                    </Select>
                  )}
                </Field>
              </div>
              <fieldset className="space-y-2">
                <legend className="mb-1 flex items-center gap-1 text-sm font-medium">
                  {t('admin.promotions.tiers')}
                  <HelpTip k="promotions.tiers" />
                </legend>
                {draft.tiers.map((tier, i) => (
                  <div key={i} className="flex flex-wrap items-end gap-2">
                    <Field
                      label={
                        draft.basis === 'subtotal'
                          ? t('admin.promotions.tierMinAmount')
                          : t('admin.promotions.tierMinQty')
                      }
                      className="w-40"
                    >
                      {(p) => (
                        <Input
                          {...p}
                          inputMode="decimal"
                          value={tier.min}
                          onChange={(e) =>
                            set({
                              tiers: draft.tiers.map((x, j) => (j === i ? { ...x, min: e.target.value } : x)),
                            })
                          }
                        />
                      )}
                    </Field>
                    <Field
                      label={
                        draft.tierType === 'percentage'
                          ? t('admin.pricing.percent')
                          : t('admin.pricing.amount')
                      }
                      className="w-40"
                    >
                      {(p) => (
                        <Input
                          {...p}
                          inputMode="decimal"
                          value={tier.value}
                          onChange={(e) =>
                            set({
                              tiers: draft.tiers.map((x, j) =>
                                j === i ? { ...x, value: e.target.value } : x,
                              ),
                            })
                          }
                        />
                      )}
                    </Field>
                    {draft.tiers.length > 1 && (
                      <span className="inline-flex items-center gap-1 pb-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-label={t('admin.promotions.removeTier', { n: i + 1 })}
                          onClick={() => set({ tiers: draft.tiers.filter((_, j) => j !== i) })}
                        >
                          <Trash2 className="h-4 w-4" aria-hidden />
                        </Button>
                        <HelpTip k="promotions.remove_tier" />
                      </span>
                    )}
                  </div>
                ))}
                {fe('rules.tiers') && (
                  <p role="alert" className="text-sm font-medium text-rose">
                    {fe('rules.tiers')}
                  </p>
                )}
                {draft.tiers.length < 5 && (
                  <span className="inline-flex items-center gap-1">
                    <Button
                      size="sm"
                      onClick={() => set({ tiers: [...draft.tiers, { min: '', value: '' }] })}
                    >
                      <Plus className="h-4 w-4" aria-hidden />
                      {t('admin.promotions.addTier')}
                    </Button>
                    <HelpTip k="promotions.add_tier" />
                  </span>
                )}
              </fieldset>
            </div>
          )}
          {needsCurrency(draft) && (
            <Field
              label={t('admin.promotions.currency')}
              help={<HelpTip k="promotions.currency" />}
              error={fe('currency')}
            >
              {(p) => (
                <Select
                  {...p}
                  className="max-w-[8rem]"
                  value={draft.currency}
                  onChange={(e) => set({ currency: e.target.value })}
                >
                  {CURRENCIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          )}

          {targetable && (
            <Section
              title={t('admin.promotions.targets')}
              description={t('admin.promotions.targetsLead')}
              action={<HelpTip k="promotions.targets" />}
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <CheckList
                  legend={t('admin.promotions.categories')}
                  help={<HelpTip k="promotions.categories" />}
                  items={categories.map((c) => ({ id: c.id, name: c.name }))}
                  value={draft.categoryIds}
                  onChange={(v) => set({ categoryIds: v })}
                />
                <CheckList
                  legend={t('admin.promotions.brands')}
                  help={<HelpTip k="promotions.brands" />}
                  items={brands.map((b) => ({ id: b.id, name: b.name }))}
                  value={draft.brandIds}
                  onChange={(v) => set({ brandIds: v })}
                />
                <CheckList
                  legend={t('admin.promotions.collections')}
                  help={<HelpTip k="promotions.collections" />}
                  items={(collections.data ?? []).map((c) => ({ id: c.id, name: c.title }))}
                  value={draft.collectionIds}
                  onChange={(v) => set({ collectionIds: v })}
                  empty={t('admin.promotions.noCollections')}
                />
              </div>
              <div className="mt-4">
                <ProductMultiPicker
                  label={t('admin.promotions.products')}
                  help={<HelpTip k="promotions.products" />}
                  value={draft.productIds}
                  onChange={(v) => set({ productIds: v })}
                  error={fe('targets.productIds')}
                  max={500}
                />
              </div>
            </Section>
          )}

          <Section title={t('admin.promotions.audienceTitle')} action={<HelpTip k="promotions.audience" />}>
            <div className="grid gap-4">
              <Field label={t('admin.promotions.audience')}>
                {(p) => (
                  <Select
                    {...p}
                    className="max-w-xs"
                    value={draft.companies.length ? 'dealers' : draft.audience}
                    disabled={draft.companies.length > 0}
                    onChange={(e) => set({ audience: e.target.value as Draft['audience'] })}
                  >
                    {(['all', 'retail', 'dealers'] as const).map((a) => (
                      <option key={a} value={a}>
                        {t(`admin.promotions.audiences.${a}`)}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <CheckList
                legend={t('admin.promotions.groups')}
                help={<HelpTip k="promotions.groups" />}
                items={(groups.data ?? []).map((g) => ({ id: g.id, name: g.name }))}
                value={draft.customerGroupIds}
                onChange={(v) => set({ customerGroupIds: v })}
              />
              {can('b2b.dealer.read') && (
                <CompanyMultiPicker
                  label={t('admin.dealerCampaigns.companies')}
                  help={<HelpTip k="promotions.companies" />}
                  value={draft.companies}
                  onChange={(v) => set({ companies: v })}
                  error={fe('companyIds')}
                />
              )}
              {draft.companies.length > 0 && (
                <p className="text-sm text-ink-2">{t('admin.dealerCampaigns.companiesNote')}</p>
              )}
              {can('marketing.audience.read') && (
                <CheckList
                  legend={t('admin.promotions.segments')}
                  help={<HelpTip k="promotions.segments" />}
                  items={(segments.data ?? []).map((s) => ({ id: s.id, name: s.name }))}
                  value={draft.segmentIds}
                  onChange={(v) => set({ segmentIds: v })}
                  empty={t('admin.promotions.noSegments')}
                />
              )}
            </div>
          </Section>

          <Section title={t('admin.promotions.rulesTitle')} action={<HelpTip k="promotions.stacking" />}>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label={t('admin.promotions.priority')}
                help={<HelpTip k="promotions.priority" />}
                error={fe('priority')}
              >
                {(p) => (
                  <Input
                    {...p}
                    inputMode="numeric"
                    value={draft.priority}
                    onChange={(e) => set({ priority: e.target.value })}
                  />
                )}
              </Field>
              <Field label={t('admin.promotions.stacking')}>
                {(p) => (
                  <Select
                    {...p}
                    value={draft.stacking}
                    onChange={(e) => set({ stacking: e.target.value as Draft['stacking'] })}
                  >
                    <option value="combinable">{t('admin.promotions.stackings.combinable')}</option>
                    <option value="exclusive">{t('admin.promotions.stackings.exclusive')}</option>
                  </Select>
                )}
              </Field>
              <label className="flex items-center gap-2 text-sm sm:col-span-2">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={draft.allowCoupon}
                  onChange={(e) => set({ allowCoupon: e.target.checked })}
                />
                {t('admin.promotions.allowCoupon')}
                <HelpTip k="promotions.allow_coupon" />
              </label>
              <Field
                label={t('admin.promotions.startsAt')}
                optionalLabel={t('common.optional')}
                help={<HelpTip k="promotions.starts_at" />}
                error={fe('startsAt')}
              >
                {(p) => (
                  <Input
                    {...p}
                    type="datetime-local"
                    value={draft.startsAt}
                    onChange={(e) => set({ startsAt: e.target.value })}
                  />
                )}
              </Field>
              <Field
                label={t('admin.promotions.endsAt')}
                optionalLabel={t('common.optional')}
                help={<HelpTip k="promotions.ends_at" />}
                error={fe('endsAt')}
              >
                {(p) => (
                  <Input
                    {...p}
                    type="datetime-local"
                    value={draft.endsAt}
                    onChange={(e) => set({ endsAt: e.target.value })}
                  />
                )}
              </Field>
            </div>
          </Section>
        </fieldset>

        <section aria-labelledby="promo-preview-title" className="space-y-3">
          <h2 id="promo-preview-title" className="flex items-center gap-1 font-medium">
            {t('admin.promotions.preview')}
            <HelpTip k="promotions.preview" />
          </h2>
          <div className="rounded-md border-s-4 border-pine bg-pine-tint px-3 py-2 text-sm">
            <p className="font-semibold break-words">{draft.label || t('admin.promotions.previewLabel')}</p>
            {ruleLines(preview).map((line) => (
              <p key={line} className="text-ink-2">
                {line}
              </p>
            ))}
            {draft.endsAt && (
              <p className="text-ink-3">
                {t('storefront.promo.until', { date: formatDate(new Date(draft.endsAt).toISOString()) })}
              </p>
            )}
          </div>
          <p className="text-sm text-ink-3">{t('admin.promotions.previewNote')}</p>
        </section>
      </div>

      {promotion && (
        <ContentReviewPanel
          status={promotion.review.status}
          note={promotion.review.note}
          findings={promotion.review.findings}
          required={promotion.review.required}
          dirty={dirty}
          canReview={can('content.expert_review')}
          busy={review.isPending}
          onDecide={(decision, note) => review.mutate({ decision, note })}
        />
      )}

      {promotion && promotion.kind !== 'free_shipping' && (
        <Section
          title={t('admin.promotions.excludedTitle')}
          description={t('admin.promotions.excludedLead', { days: promotion.referenceDays })}
          action={<HelpTip k="promotions.excluded" />}
        >
          {!promotion.excluded.total ? (
            <p className="text-sm text-ink-2">{t('admin.promotions.excludedNone')}</p>
          ) : (
            <div className="space-y-2">
              <p className="text-sm font-medium">
                {t('admin.promotions.excludedCount', { count: promotion.excluded.total })}
              </p>
              <div className="overflow-x-auto rounded-md border border-line">
                <table className="w-full text-sm" aria-label={t('admin.promotions.excludedTitle')}>
                  <thead className="bg-surface-2 text-ink-2">
                    <tr>
                      <th className="px-3 py-2 text-start font-medium">{t('admin.promotions.colProduct')}</th>
                      <th className="px-3 py-2 text-start font-medium">
                        {t('admin.promotions.colPriceList')}
                      </th>
                      <th className="px-3 py-2 text-end font-medium">{t('admin.promotions.colCurrent')}</th>
                      <th className="px-3 py-2 text-end font-medium">{t('admin.promotions.colLowest')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {promotion.excluded.items.map((x) => (
                      <tr key={`${x.sku}-${x.priceList}`} className="border-t border-line">
                        <td className="px-3 py-2">
                          {x.title} <span className="text-ink-3">{x.sku}</span>
                        </td>
                        <td className="px-3 py-2">{x.priceList}</td>
                        <td className="px-3 py-2 text-end tabular-nums">
                          {money(x.amountMinor, x.currency)}
                        </td>
                        <td className="px-3 py-2 text-end tabular-nums">
                          {money(x.lowestMinor, x.currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Section>
      )}

      {promotion && (
        <Section title={t('admin.promotions.usageTitle')} action={<HelpTip k="promotions.usage" />}>
          {!promotion.usage.length ? (
            <p className="text-sm text-ink-2">{t('admin.promotions.usageNone')}</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {promotion.usage.map((u) => (
                <li key={u.currency}>
                  {t('admin.promotions.usageLine', {
                    orders: u.orders,
                    discount: money(u.discountMinor, u.currency),
                    revenue: money(u.revenueMinor, u.currency),
                  })}
                </li>
              ))}
            </ul>
          )}
        </Section>
      )}

      {promotion && canWrite && promotion.status === 'draft' && !promotion.periods.length && (
        <Section title={t('admin.promotions.dangerZone')}>
          <div className="flex items-center gap-2">
            <ConfirmButton
              message={t('admin.promotions.deleteConfirm', { name: promotion.name })}
              onConfirm={() => remove.mutateAsync(undefined).catch(() => undefined)}
            >
              {t('common.delete')}
            </ConfirmButton>
            <HelpTip k="promotions.delete" />
          </div>
        </Section>
      )}
      {promotion && (
        <p className="text-sm text-ink-3">
          {t('admin.promotions.updated', { date: formatDate(promotion.updatedAt) })}
        </p>
      )}
    </PageFrame>
  );
}
